import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, GraduationCap, Sparkles, BookOpen, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

/* -------------------------------------------------------------------------- */
/*  Per-persona Lesson History                                                */
/*                                                                            */
/*  Renders every daily lesson Shu'aib has written for a given AI advisor.    */
/*  Endpoint:  GET /api/ai/mentors/lessons/{persona}                          */
/*  Each lesson advances the persona by +10 years of accumulated mastery.     */
/* -------------------------------------------------------------------------- */

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' }); }
  catch { return iso.slice(0, 10); }
}

export default function PersonaLessons() {
  const { personaId } = useParams();
  const [persona, setPersona] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [age, setAge] = useState(null);
  const [field, setField] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/ai/employees/').then(({ data }) => data),
      api.get(`/ai/mentors/lessons/${personaId}`).then(({ data }) => data).catch((e) => {
        toast.error(e?.response?.data?.detail || 'Could not load lessons.');
        return { lessons: [] };
      }),
      api.get('/ai/mentors/status').then(({ data }) => data).catch(() => ({ personas: [] })),
    ])
      .then(([emp, les, men]) => {
        const p = emp.find((x) => x.id === personaId);
        setPersona(p || null);
        setLessons(les?.lessons || []);
        const found = (men.personas || []).find((c) => c.persona === personaId);
        if (found) {
          setAge(found.age_years);
          setField(found.field);
        }
      })
      .finally(() => setLoading(false));
  }, [personaId]);

  // Group lessons by year-month
  const grouped = useMemo(() => {
    const out = {};
    for (const l of lessons) {
      const key = (l.created_at || '').slice(0, 7) || 'unknown';
      (out[key] ||= []).push(l);
    }
    return out;
  }, [lessons]);

  const groupKeys = Object.keys(grouped).sort().reverse();

  return (
    <div className="ve-section" data-testid="persona-lessons-page">
      <Link
        to={`/dashboard/ai-employees/${personaId}`}
        className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#5E1F8B] font-bold uppercase tracking-[0.18em]"
        data-testid="persona-lessons-back"
      >
        <ArrowLeft size={12} /> Back to {persona?.english_name || 'advisor'}
      </Link>

      {/* Header */}
      <div className="mt-4 ve-card p-5 lg:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4 min-w-0">
            <div className="h-14 w-14 rounded-full border-2 border-[#8B3FC5] bg-black flex items-center justify-center shrink-0">
              <GraduationCap size={26} className="text-[#8B3FC5]" />
            </div>
            <div className="min-w-0">
              <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5]">Lesson history</div>
              <h1 className="display text-2xl lg:text-3xl mt-1 font-bold">
                {persona?.english_name || personaId}
                {persona?.arabic_name && (
                  <span className="ml-3 text-[#8B3FC5] text-xl lg:text-2xl" dir="rtl">{persona.arabic_name}</span>
                )}
              </h1>
              {persona?.english_role && (
                <div className="mt-1 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">{persona.english_role}</div>
              )}
              {field && (
                <p className="mt-2 text-sm text-slate-600 dark:text-white/65 max-w-xl">
                  Studying <span className="font-semibold">{field}</span>{age != null && <> — currently <span className="font-semibold">{Number(age).toLocaleString()} years</span> of accumulated mastery.</>}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold px-2.5 py-1 rounded bg-[#FFF8E5] text-[#7A5A00] dark:bg-[#3a2f0a] dark:text-[#F5E1A4]">
              <Sparkles size={11} /> {lessons.length} lessons
            </span>
            {age != null && (
              <span className="text-[10px] uppercase tracking-[0.22em] font-bold px-2.5 py-1 rounded bg-black text-[#8B3FC5] border border-[#8B3FC5]/40">
                Age {Number(age).toLocaleString()}y
              </span>
            )}
          </div>
        </div>
      </div>

      {loading && (
        <div className="mt-6 inline-flex items-center gap-2 text-sm text-slate-500" data-testid="persona-lessons-loading">
          <Loader2 size={14} className="animate-spin" /> Loading lessons…
        </div>
      )}

      {!loading && lessons.length === 0 && (
        <div className="mt-6 ve-card p-6 text-center" data-testid="persona-lessons-empty">
          <BookOpen size={24} className="text-slate-300 dark:text-white/20 mx-auto" />
          <p className="mt-2 text-sm text-slate-500 dark:text-white/55">No lessons have been written for this advisor yet. The next daily training cycle will start the record.</p>
        </div>
      )}

      {!loading && lessons.length > 0 && (
        <div className="mt-6 space-y-8">
          {groupKeys.map((k) => (
            <section key={k}>
              <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5] mb-3">
                {k === 'unknown' ? 'undated' : new Date(`${k}-01`).toLocaleDateString(undefined, { year: 'numeric', month: 'long' })}
                <span className="ml-2 text-slate-400 dark:text-white/35 normal-case tracking-normal">· {grouped[k].length}</span>
              </div>
              <ol className="relative border-l-2 border-[#8B3FC5]/25 dark:border-[#8B3FC5]/30 ml-2 space-y-4">
                {grouped[k].map((l, i) => (
                  <li
                    key={`${l.created_at || ''}-${i}`}
                    data-testid={`persona-lesson-${k}-${i}`}
                    className="pl-5 relative"
                  >
                    <span className="absolute -left-[7px] top-1.5 h-3 w-3 rounded-full bg-[#8B3FC5] shadow-[0_0_10px_rgba(174,82,234,0.6)]" />
                    <div className="ve-card p-4 lg:p-5">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-white/45 font-bold">
                          {fmtDate(l.created_at)}{l.age_years_after != null && <> · advanced to <span className="text-[#5E1F8B] dark:text-[#8B3FC5]">{Number(l.age_years_after).toLocaleString()}y</span></>}
                        </div>
                        {l.cycle_number != null && (
                          <span className="text-[9px] uppercase tracking-[0.22em] font-bold px-2 py-0.5 rounded bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-white/55">
                            cycle #{l.cycle_number}
                          </span>
                        )}
                      </div>
                      {l.new_principle && (
                        <p className="mt-2 text-base font-semibold text-slate-900 dark:text-white" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
                          {l.new_principle}
                        </p>
                      )}
                      {l.lesson_for_addendum && (
                        <p className="mt-2 text-sm leading-relaxed text-slate-700 dark:text-white/75">
                          {l.lesson_for_addendum}
                        </p>
                      )}
                      {l.taught_by && (
                        <div className="mt-2 text-[10px] uppercase tracking-[0.22em] text-slate-400 dark:text-white/40">
                          taught by <span className="font-semibold text-slate-500 dark:text-white/55">{l.taught_by}</span>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
